import { AppProps } from 'next/app'
import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Provider } from 'react-redux'
import { SessionProvider } from 'next-auth/react'
import { AuthProvider } from 'providers/AuthProvider'
import { EcboFeatureFlagsProvider } from 'providers/EcboFeatureFlagsProvider'
import { IntercomProvider } from 'react-use-intercom'
import moment from 'moment'
import { LoadScriptNext } from '@react-google-maps/api'

import { initializeApp } from 'firebase/app'
import { ToastContainer } from 'react-toastify'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { parseCookies } from 'nookies'

import { appWithTranslation, useTranslation } from 'next-i18next'
import { createStore } from 'state/createStore'
import { refreshErrors } from 'state/ducks/app/actions'
import * as gtag from 'lib/gtag'
import { authClient } from 'lib/axios'
import useScrollRestoration from 'hooks/common/useScrollRestoration'
import { useUtmTracking } from 'features/tracking/hooks/useUtmTracking'

import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-multi-carousel/lib/styles.css'
import 'react-circular-progressbar/dist/styles.css'
import '../styles/globals.scss'
import '../styles/calendar.scss'

const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.FIREBASE_DB_URL,
  projectId: process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID,
  measurementId: process.env.FIREBASE_MEASUREMENT_ID,
}

initializeApp(firebaseConfig)

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY)

declare global {
  interface Window {
    Intercom: any
  }
}

const store = createStore()
const googleMapLibraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places']

// @ts-ignore Property 'err' does not exist
function App({ Component, pageProps, err }: AppProps) {
  const cookies = parseCookies()
  const router = useRouter()
  const { i18n } = useTranslation()

  useScrollRestoration()
  useUtmTracking()

  /**
   * Intercomの表示判定
   * @param route string router.route
   * @returns boolean true / false 表示する場合はtrue, 非表示はfalse
   */
  const isShowIntercom = (route: string): boolean => {
    const paths = ['/', '/jpn/city/[...slug]']
    return paths.includes(route)
  }

  useEffect(() => {
    const handleRouteChange = (_) => {
      store.dispatch(refreshErrors())
    }
    const handleRouteChangeComplete = (_) => {}
    const handleRouteChangeError = (_) => {}
    router.events.on('routeChangeStart', handleRouteChange)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', handleRouteChangeError)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', handleRouteChangeError)
    }
  }, [router, store])

  useEffect(() => {
    if (!gtag.GA_TRACKING_ID) return

    const handleRouteChange = (url: string) => {
      gtag.pageview(url)
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    window.Intercom('update', {
      hide_default_launcher: !isShowIntercom(router.route),
    })
  }, [router])

  const handleCheckMaintainStatus = useCallback(async () => {
    try {
      if (router.pathname === '/maintain') return

      const result = await authClient.get('/maintenance')

      const { status, maintenance_start_time, maintenance_end_time } = result.data

      if (status === 'maintenance') {
        let startDate = maintenance_start_time ? moment(maintenance_start_time).valueOf() : ''
        let endDate = maintenance_end_time ? moment(maintenance_end_time).valueOf() : ''
        router.replace(`/maintain?start=${startDate}&end=${endDate}`)
      }
    } catch (error) {
      console.log(error)
    }
  }, [router])

  useEffect(() => {
    handleCheckMaintainStatus()
  }, [handleCheckMaintainStatus])

  useEffect(() => {
    const i18nLanguage = i18n.language
    const configuredLanguage = localStorage.getItem('language')
    const clientLanguage = navigator.language

    if (configuredLanguage) {
      if (configuredLanguage === 'ja' && i18nLanguage !== 'ja') {
        window.location.href = `${router.asPath}`
        return
      }

      if (configuredLanguage !== i18nLanguage) {
        window.location.href = `/${configuredLanguage}/${router.asPath}`
        return
      }
    } else {
      const isJapaneseBrowser = clientLanguage.startsWith('ja')
      const isTaiwanBrowser = clientLanguage.startsWith('zh-TW')
      const isEnglishBrowser = clientLanguage.startsWith('en')

      if (isJapaneseBrowser) {
        window.location.href = `${router.asPath}`
        localStorage.setItem('language', 'ja')
        return
      }

      if (isTaiwanBrowser) {
        window.location.href = `/zh-TW/${router.asPath}`
        localStorage.setItem('language', 'zh-TW')
        return
      }

      if (isEnglishBrowser) {
        window.location.href = `/en/${router.asPath}`
        localStorage.setItem('language', 'en')
        return
      }

      window.location.href = `/en/${router.asPath}`
      localStorage.setItem('language', 'en')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Provider store={store}>
      <EcboFeatureFlagsProvider>
        <Elements stripe={stripePromise}>
          <SessionProvider session={(pageProps as any).session}>
            <AuthProvider>
              {/* @see: Workaround for https://github.com/vercel/next.js/issues/8592 */}
              <IntercomProvider appId={process.env.INTERCOM_APP_ID}>
                <LoadScriptNext
                  googleMapsApiKey={process.env.GOOGLE_MAP_KEY}
                  libraries={googleMapLibraries}
                  loadingElement={<div />}
                >
                  <div>
                    <ToastContainer theme="colored" hideProgressBar autoClose={2000} />
                    <Component {...pageProps} err={err}></Component>
                  </div>
                </LoadScriptNext>
              </IntercomProvider>
            </AuthProvider>
          </SessionProvider>
        </Elements>
      </EcboFeatureFlagsProvider>
    </Provider>
  )
}

export default appWithTranslation(App)
